import React, { useEffect, useState } from 'react';
import MESSAGES from '../../../../common/data/constants/messages';
import { IJobTitle } from '../../../../common/data/dtos/job-title';
import Badge from '../../../../components/bootstrap/Badge';
import Button from '../../../../components/bootstrap/Button';
import Card, { CardActions, CardBody, CardHeader, CardLabel, CardTitle } from '../../../../components/bootstrap/Card';
import { MoreActions } from '../../../../components/extras/MoreActions';
import { showSuccess, showWarning } from '../../../../components/extras/Notifications';
import PaginationButtons, { dataPagination, PER_COUNT } from '../../../../components/PaginationButtons';
import jobTitleService from '../../../../services/job-title-service';
import LoadingPage from '../../loading/LoadingPage';
import { TrainingJobTitleModal } from '../../training-job-title/Common/TrainingJobTitleModal';
import { WorkResourceJobTitleModal } from '../../work-resource-job-title/Common/WorkResourceJobTitleModal';
import { JobTitleModal } from './JobTitleModal';

export const CommonJobTitleList = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(PER_COUNT['10']);

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isModalOpened, setIsModalOpened] = useState(false);
    const [isModalWorkResouceOpened, setIsModalWorkResouceOpened] = useState(false);
    const [isModalTrainingOpened, setIsModalTrainingOpened] = useState(false);

    const [jobTitles, setJobTitles] = useState<IJobTitle[]>([]);
    const [selectedJobTitle, setSelectedJobTitle] = useState<IJobTitle | null>(null);

    useEffect(() => {
        fetchJobTitles();
    }, []);

    const fetchJobTitles = async () => {
        setIsLoading(true);

        const response = await jobTitleService.getJobTitles();

        setIsLoading(false);

        if (response.error) {
            showWarning(response.error);
            return;
        }

        setJobTitles(response.jobTitles || []);
    };

    const toggleModal = (setter: React.Dispatch<React.SetStateAction<boolean>>, isVisible: boolean) => {
        setter(isVisible);
    };

    const handleAddClick = () => {
        toggleModal(setIsModalOpened, true);
    };

    const onModalClose = () => {
        toggleModal(setIsModalOpened, false);
        setSelectedJobTitle(null);
    };

    const onWorkResouceModalClose = () => {
        toggleModal(setIsModalWorkResouceOpened, false);
        setSelectedJobTitle(null);
    };

    const onTrainingModalClose = () => {
        toggleModal(setIsModalTrainingOpened, false);
        setSelectedJobTitle(null);
    };

    const onAfterSubmit = async () => {
        await fetchJobTitles();
        onModalClose();
        onWorkResouceModalClose();
    };

    const onWorkResouceAfterSubmit = async () => {
        await fetchJobTitles();
        onWorkResouceModalClose();
    };

    const onTrainingAfterSubmit = async () => {
        await fetchJobTitles();
        onTrainingModalClose();
    };

    const handleDeleteClick = async (jobTitle: IJobTitle) => {
        if (!jobTitle.id) {
            showWarning(MESSAGES.ACTION.DELETE_ID_ERROR);
            return;
        }

        setIsLoading(true);

        const response = await jobTitleService.deleteJobTitle(jobTitle.id);

        setIsLoading(false);

        if (response.error) {
            showWarning(response.error);
            return;
        }

        showSuccess(MESSAGES.ACTION.DELETED);
        await fetchJobTitles();
    };

    const handleEditClick = async (jobTitle: IJobTitle) => {
        setSelectedJobTitle(jobTitle);
        toggleModal(setIsModalOpened, true);
    };

    const handleWorkResouceClick = async (jobTitle: IJobTitle | null) => {
        setSelectedJobTitle(jobTitle);
        toggleModal(setIsModalWorkResouceOpened, true);
    };

    const handleTrainingClick = async (jobTitle: IJobTitle | null) => {
        setSelectedJobTitle(jobTitle);
        toggleModal(setIsModalTrainingOpened, true);
    };

    if (isLoading) {
        return <LoadingPage />;
    }

    return (
        <>
            <Card style={{ minHeight: 600 }}>
                <CardHeader borderSize={1}>
                    <CardLabel icon="Work" iconColor="primary">
                        <CardTitle tag="div" className="h5">
                            Funções
                        </CardTitle>
                    </CardLabel>
                    <CardActions>
                        <Button color="light" icon="AddCircle" onClick={handleAddClick}>
                            Adicionar
                        </Button>
                    </CardActions>
                </CardHeader>
                <CardBody className="table-responsive">
                    <table className="table table-modern table-hover">
                        <thead>
                            <tr>
                                <th>Nome</th>
                                <th>Setor</th>
                                <th>C.B.O.</th>
                                <th>Situação</th>
                                <td aria-labelledby="Actions" />
                            </tr>
                        </thead>
                        <tbody>
                            {dataPagination(jobTitles, currentPage, perPage).map((item: IJobTitle) => (
                                <tr key={item.id}>
                                    <td>{item.name}</td>
                                    <td>{item.department ?? '-'}</td>
                                    <td>{item.occupationCode ?? '-'}</td>
                                    <td>
                                        {item.status ? (
                                            <h5>
                                                <Badge color="success" className="align-items-center justify-content-center">
                                                    Ativo
                                                </Badge>
                                            </h5>
                                        ) : (
                                            <h5>
                                                <Badge color="danger" className="align-items-center justify-content-center">
                                                    Inativo
                                                </Badge>
                                            </h5>
                                        )}
                                    </td>

                                    <MoreActions
                                        id={item.id}
                                        showEdit
                                        showDelete
                                        onEditClick={() => handleEditClick(item)}
                                        onDeleteClick={() => handleDeleteClick(item)}
                                        customActions={[
                                            {
                                                label: 'Ferramentas e EPI',
                                                icon: 'Build',
                                                onClickAction: () => handleWorkResouceClick(item),
                                            },
                                            {
                                                label: 'Treinamentos',
                                                icon: 'School',
                                                onClickAction: () => handleTrainingClick(item),
                                            },
                                        ]}
                                    />
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </CardBody>
                <PaginationButtons data={jobTitles} label="items" setCurrentPage={setCurrentPage} currentPage={currentPage} perPage={perPage} setPerPage={setPerPage} />
            </Card>

            <JobTitleModal isModalOpened={isModalOpened} onModalClose={onModalClose} onAfterSubmit={onAfterSubmit} selectedJobTitle={selectedJobTitle} />
            <WorkResourceJobTitleModal isModalOpened={isModalWorkResouceOpened} onModalClose={onWorkResouceModalClose} onAfterSubmit={onWorkResouceAfterSubmit} selectedJobTitle={selectedJobTitle} />
            <TrainingJobTitleModal isModalOpened={isModalTrainingOpened} onModalClose={onTrainingModalClose} onAfterSubmit={onTrainingAfterSubmit} selectedJobTitle={selectedJobTitle} />
        </>
    );
};
